import React, { Suspense, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { PageLoader } from "./components/page-loader";
import { AuthenticationGuard } from "./components/middleware/authentication-guard";
import { OnboardingRoute } from "./components/middleware/onboarding-route";
import { OnboardingProvider } from "./providers/onboarding-provider";
import { NotificationProvider } from "./components/notifications/NotificationProvider";
import { PermissionsProvider } from "./providers/PermissionsProvider";

import { Route, Routes, Navigate } from "react-router-dom";
import { CallbackPage } from "./pages/callback-page";
import { LandingRouter } from "./components/LandingRouter";
import { NotFoundPage } from "./pages/not-found-page";
import { ProfilePage } from "./pages/profile-page";
import { DashboardRedirect } from "./components/DashboardRedirect";
import { VerifyEmailPage } from "./pages/verify-email-page";
import { AcceptOrgInvitePage } from "./pages/accept-org-invite-page";
import { PreScreenPage } from "./pages/onboarding/pre-screen-page";
import { PreScreenTransferPage } from "./pages/pre-screen-transfer-page";
import { PendingApprovalPage } from "./pages/onboarding/pending-approval-page";
import { VerificationPage } from "./pages/onboarding/verification-page";
import { ConsentPage } from "./pages/onboarding/consent-page";
import { AwaitCountersignPage } from "./pages/onboarding/await-countersign-page";
import { PreferencesPage } from "./pages/onboarding/preferences-page";
import { UpdatesPage } from "./pages/onboarding/updates-page";
import { ThankYouPage } from "./pages/onboarding/thank-you";

import { PermissionRoute } from "./components/middleware/permission-route";
import { SubdomainRoute } from "./components/middleware/subdomain-route";
import analyticsService from "./services/analytics.service";
import { usePageTracking } from "./hooks/usePageTracking";
import {
  AdminPage,
  ApplicationRunnerPage,
  CohortChatPage,
  CohortsPage,
  CountersignPage,
  CscDashboardPage,
  InvitePage,
  InviteRedirect,
  InviteRedemptionPage,
  LLMJobExecution,
  LLMJobExecutionsList,
  LLMJobsPage,
  LLMWorkflowExecutions,
  LighthouseNoteCreatePage,
  LighthouseNoteViewPage,
  LighthouseNotesPage,
  LighthousePage,
  McpOAuthCallbackPage,
  MemberFunnelPage,
  OmicsLabPage,
  OrgManagementPage,
  PhlebDashboardPage,
  PhlebProviderDashboardPage,
  PipelineDashboardPage,
  PrescreenManagerPage,
  FormManagerPage,
  RecordsPage,
  SampleCollectionsPage,
  SecurityPage,
  SurveyManagementPage,
  TeamManagementPage,
  UserDetailPage,
  DesignSystemPage,
} from "./routing/lazy-pages";

const routeFallback = <PageLoader />;

export const App = () => {
  const { isLoading, user, isAuthenticated } = useAuth0();
  
  // Initialize Google Analytics
  useEffect(() => {
    analyticsService.initialize();
  }, []);

  // Set user ID when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      analyticsService.setUserId(user.sub);
      analyticsService.setUserProperties({
        user_type: user.email?.includes('@') ? 'authenticated' : 'guest',
      });
    }
  }, [isAuthenticated, user]);

  // Track page views
  usePageTracking();

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <PermissionsProvider>
      <NotificationProvider>
        <OnboardingProvider>
          <Suspense fallback={routeFallback}>
            <Routes>
        <Route path="/" element={<LandingRouter />} />
        <Route path="/profile" element={<AuthenticationGuard component={ProfilePage} />} />
        <Route path="/dashboard" element={<AuthenticationGuard component={DashboardRedirect} />} />
        <Route path="/chat" element={<PermissionRoute permission="chat:access" component={CohortChatPage} />} />
        <Route path="/team" element={<SubdomainRoute allow="platform" component={() => <PermissionRoute permission="org_user:manage" component={TeamManagementPage} />} />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
        <Route path="/mcp-oauth/callback" element={<McpOAuthCallbackPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/accept-org-invite" element={<AcceptOrgInvitePage />} />

        {/* Invite redemption route */}
        <Route path="/invite/:token/redeem" element={<InviteRedemptionPage />} />

        {/* Passwordless Attune countersign (no Auth0) */}
        <Route path="/countersign/:token" element={<CountersignPage />} />
        
        {/* Onboarding flow routes - app subdomain only (member experience) */}
        <Route 
          path="/pre-screen" 
          element={<SubdomainRoute allow="app" component={PreScreenPage} />} 
        />
        <Route 
          path="/pre-screen/:study" 
          element={<SubdomainRoute allow="app" component={PreScreenPage} />} 
        />
        <Route 
          path="/pre-screen-transfer" 
          element={<SubdomainRoute allow="app" component={PreScreenTransferPage} />} 
        />
        <Route 
          path="/pending-approval" 
          element={
            <SubdomainRoute allow="app" component={() =>
              <AuthenticationGuard 
                component={() => <OnboardingRoute component={PendingApprovalPage} step="PENDING_APPROVAL" />} 
              />
            } />
          } 
        />
        <Route 
          path="/verify" 
          element={
            <SubdomainRoute allow="app" component={() =>
              <AuthenticationGuard 
                component={() => <OnboardingRoute component={VerificationPage} step="VERIFY" />} 
              />
            } />
          } 
        />
        <Route 
          path="/consent" 
          element={
            <SubdomainRoute allow="app" component={() =>
              <AuthenticationGuard 
                component={() => <OnboardingRoute component={ConsentPage} step="CONSENT" />} 
              />
            } />
          } 
        />
        <Route
          path="/await-countersign"
          element={
            <SubdomainRoute allow="app" component={() =>
              <AuthenticationGuard
                component={() => <OnboardingRoute component={AwaitCountersignPage} step="AWAIT_COUNTERSIGN" />}
              />
            } />
          }
        />
        <Route 
          path="/preferences" 
          element={
            <SubdomainRoute allow="app" component={() =>
              <AuthenticationGuard 
                component={() => <OnboardingRoute component={PreferencesPage} step="PREFERENCES" />} 
              />
            } />
          } 
        />
        <Route
          path="/updates"
          element={
            <SubdomainRoute allow="app" component={() =>
              <AuthenticationGuard
                component={() => <OnboardingRoute component={UpdatesPage} step="UPDATES" />}
              />
            } />
          }
        />
        <Route
          path="/phleb/collections"
          element={
            <PermissionRoute
              permission="sample_collection:provider_read"
              component={PhlebProviderDashboardPage}
            />
          }
        />
        <Route 
          path="/thank-you" 
          element={
            <SubdomainRoute allow="app" component={() =>
              <AuthenticationGuard 
                component={() => <OnboardingRoute component={ThankYouPage} step="DONE" />} 
              />
            } />
          } 
        />
        
        {/* Admin routes — page modules are code-split via routing/lazy-pages */}
        <Route
          path="/admin/design-system"
          element={<PermissionRoute permission={["admin:survey_management", "admin:invitations", "admin:workflows"]} component={DesignSystemPage} />}
        />
        <Route 
          path="/admin/lighthouse"
          element={<PermissionRoute permission={["admin:survey_management", "admin:invitations"]} component={LighthousePage} />}
        />
        <Route
          path="/admin/lighthouse/notes/:userId"
          element={<PermissionRoute permission={["admin:survey_management", "admin:invitations"]} component={LighthouseNotesPage} />}
        />
        <Route
          path="/admin/lighthouse/notes/:userId/new"
          element={<PermissionRoute permission={["admin:survey_management", "admin:invitations"]} component={LighthouseNoteCreatePage} />}
        />
        <Route
          path="/admin/lighthouse/notes/:userId/note/:noteId"
          element={<PermissionRoute permission={["admin:survey_management", "admin:invitations"]} component={LighthouseNoteViewPage} />}
        />
        <Route
          path="/admin/survey-management"
          element={<PermissionRoute permission="admin:survey_management" component={SurveyManagementPage} />}
        />
        <Route
          path="/admin/invite"
          element={
            process.env.REACT_APP_LIGHTHOUSE_PLATFORM === 'true'
              ? <InviteRedirect />
              : <PermissionRoute permission="admin:invitations" component={InvitePage} />
          }
        />
        <Route
          path="/admin/sample-collections"
          element={<PermissionRoute permission="admin:member_funnel" component={MemberFunnelPage} />}
        />
        <Route
          path="/admin/sample-intake"
          element={<PermissionRoute permission="admin:sample_intake" component={SampleCollectionsPage} />}
        />
        <Route 
          path="/admin/users" 
          element={<PermissionRoute permission="admin:current_members" component={AdminPage} />} 
        />
        <Route 
          path="/admin/pre-screen" 
          element={<PermissionRoute permission="admin:prescreen" component={PrescreenManagerPage} />} 
        />
        <Route
          path="/admin/forms"
          element={<PermissionRoute permission="admin:form_management" component={FormManagerPage} />}
        />
        <Route 
          path="/admin/security" 
          element={<PermissionRoute permission={["admin:security", "admin:provisioning"]} component={SecurityPage} />} 
        />
        
        <Route 
          path="/admin/provisioning" 
          element={<Navigate to="/admin/security" replace />} 
        />
        
        <Route 
          path="/admin/organization" 
          element={<PermissionRoute permission="org_user:manage" component={OrgManagementPage} />} 
        />

        <Route 
          path="/admin/users/:id" 
          element={<PermissionRoute permission="admin:user_detail" component={UserDetailPage} />} 
        />
        <Route
          path="/admin/cohorts"
          element={<PermissionRoute permission="study:read" component={CohortsPage} />}
        />
        <Route
          path="/admin/cohorts/:studyId"
          element={<PermissionRoute permission="study:read" component={CohortsPage} />}
        />
        
        <Route
          path="/admin/cohort-chat"
          element={<PermissionRoute permission="chat:access" component={CohortChatPage} />}
        />
        <Route
          path="/admin/omics/:projectId?"
          element={<PermissionRoute permission="omics:access" component={OmicsLabPage} />}
        />
        
        <Route
          path="/admin/records"
          element={<PermissionRoute permission="records_v2:read" component={RecordsPage} />}
        />
        <Route
          path="/admin/pipeline"
          element={<PermissionRoute permission="admin:pipeline" component={PipelineDashboardPage} />}
        />
        
        {/* LLM Jobs/Workflows routes */}
        <Route path="/admin/llm-jobs" element={<PermissionRoute permission="admin:workflows" component={LLMJobsPage} />} />
        <Route path="/admin/prompt-lab" element={<Navigate to="/admin/pipeline?tab=prompt-lab" replace />} />
        <Route path="/admin/llm-jobs/:workflowId/executions" element={<PermissionRoute permission="admin:workflows" component={LLMWorkflowExecutions} />} />
        <Route path="/admin/llm-jobs/executions" element={<PermissionRoute permission="admin:workflows" component={LLMJobExecutionsList} />} />
        <Route path="/admin/llm-jobs/executions/:executionId" element={<PermissionRoute permission="admin:workflows" component={LLMJobExecution} />} />

        {/* Clinic / iPad routes (PR 5 + PR 6) */}
        <Route path="/clinic" element={<SubdomainRoute allow="clinic" component={() => <AuthenticationGuard component={CscDashboardPage} />} />} />
        <Route path="/clinic/application/:sessionId" element={<SubdomainRoute allow="clinic" component={() => <AuthenticationGuard component={ApplicationRunnerPage} />} />} />
        <Route path="/clinic/phleb" element={<SubdomainRoute allow="clinic" component={() => <AuthenticationGuard component={PhlebDashboardPage} />} />} />

        <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </OnboardingProvider>
      </NotificationProvider>
    </PermissionsProvider>
  );
};
